import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { FolderGit2, Plus, Edit, Save, Trash2, RefreshCw } from 'lucide-react';
import { API_BASE, getAdminToken, colorMap } from './adminUtils';

export const ProjectsTab = () => {
  const [adminProjects, setAdminProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectStats, setProjectStats] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({ title: '', short_description: '', category: 'Linux', difficulty: 'beginner', estimated_time: '', prerequisites: '', sections: [{ title: '', content: '' }], order: 0 });
  const [savingProject, setSavingProject] = useState(false);

  const fetchAdminProjects = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setProjectsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/projects`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setAdminProjects((await res.json()).projects);
    } catch (e) { console.error(e); }
    finally { setProjectsLoading(false); }
  }, []);

  const fetchProjectStats = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/projects/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setProjectStats(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAdminProjects(); fetchProjectStats(); }, []);

  const resetProjectForm = () => {
    setProjectForm({ title: '', short_description: '', category: 'Linux', difficulty: 'beginner', estimated_time: '', prerequisites: '', sections: [{ title: '', content: '' }], order: 0 });
    setEditingProject(null);
    setShowProjectForm(false);
  };

  const handleSaveProject = async () => {
    if (!projectForm.title.trim()) { toast.error('Title required'); return; }
    const validSections = projectForm.sections.filter(s => s.title.trim() || s.content.trim());
    if (validSections.length === 0) { toast.error('Add at least one section with content'); return; }
    setSavingProject(true);
    try {
      const url = editingProject ? `${API_BASE}/api/admin/projects/${editingProject}` : `${API_BASE}/api/admin/projects`;
      const method = editingProject ? 'PUT' : 'POST';
      const payload = { ...projectForm, sections: validSections, content: validSections.map(s => s.title + '\n' + s.content).join('\n\n') };
      const res = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${getAdminToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(editingProject ? 'Project updated' : d.message);
      resetProjectForm();
      fetchAdminProjects(); fetchProjectStats();
    } catch (e) { toast.error(e.message); }
    finally { setSavingProject(false); }
  };

  const handleEditProject = (p) => {
    const sections = p.sections && p.sections.length > 0 ? p.sections : [{ title: 'Content', content: p.content || '' }];
    setProjectForm({ title: p.title, short_description: p.short_description, category: p.category, difficulty: p.difficulty, estimated_time: p.estimated_time, prerequisites: p.prerequisites, sections: sections, order: p.order });
    setEditingProject(p.id);
    setShowProjectForm(true);
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project permanently?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/projects/${projectId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${getAdminToken()}` } });
      if (res.ok) { toast.success('Project deleted'); fetchAdminProjects(); fetchProjectStats(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleToggleProject = async (projectId) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/projects/${projectId}/toggle`, { method: 'POST', headers: { 'Authorization': `Bearer ${getAdminToken()}` } });
      if (res.ok) { toast.success('Status updated'); fetchAdminProjects(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleToggleFreeProject = async (projectId) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/projects/${projectId}/toggle-free`, { method: 'POST', headers: { 'Authorization': `Bearer ${getAdminToken()}` } });
      if (res.ok) { toast.success('Free status updated'); fetchAdminProjects(); }
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      {projectStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total', value: projectStats.total, color: 'blue' },
            { label: 'Enabled', value: projectStats.enabled, color: 'emerald' },
            { label: 'Free', value: projectStats.free, color: 'amber' },
            { label: 'Buyers', value: projectStats.buyers, color: 'purple' },
            { label: 'Completions', value: projectStats.completions, color: 'red' },
          ].map((s, i) => (
            <Card key={i} className={`${colorMap[s.color].card} backdrop-blur`}><CardContent className="p-4"><p className={`text-xs ${colorMap[s.color].text}`}>{s.label}</p><p className="text-2xl font-bold text-white">{s.value}</p></CardContent></Card>
          ))}
        </div>
      )}

      {/* Create/Edit Form */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2"><Plus className="w-5 h-5 text-orange-400" />{editingProject ? 'Edit Project' : 'Add Project'}</CardTitle>
            <div className="flex gap-2">
              {!showProjectForm && <Button size="sm" onClick={() => setShowProjectForm(true)} className="bg-gradient-to-r from-orange-500 to-red-500 text-white" data-testid="add-project-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>}
              <Button size="sm" variant="outline" onClick={() => { fetchAdminProjects(); fetchProjectStats(); }} className="border-white/20 text-white"><RefreshCw className="w-3 h-3" /></Button>
            </div>
          </div>
        </CardHeader>
        {showProjectForm && (
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400 block mb-1">Title *</label><Input value={projectForm.title} onChange={e => setProjectForm(f => ({ ...f, title: e.target.value }))} placeholder="Project title" className="bg-white/10 border-white/20 text-white" data-testid="project-title-input" /></div>
                <div><label className="text-xs text-slate-400 block mb-1">Short Description</label><Input value={projectForm.short_description} onChange={e => setProjectForm(f => ({ ...f, short_description: e.target.value }))} placeholder="One line summary" className="bg-white/10 border-white/20 text-white" /></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><label className="text-xs text-slate-400 block mb-1">Category</label>
                  <select value={projectForm.category} onChange={e => setProjectForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="project-category-select">
                    {['Linux', 'DevOps', 'AWS', 'Terraform', 'Docker', 'Kubernetes', 'Ansible', 'Networking', 'Security', 'Python', 'Other'].map(c => <option key={c} value={c} className="bg-slate-800">{c}</option>)}
                  </select>
                </div>
                <div><label className="text-xs text-slate-400 block mb-1">Difficulty</label>
                  <select value={projectForm.difficulty} onChange={e => setProjectForm(f => ({ ...f, difficulty: e.target.value }))} className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 text-sm" data-testid="project-difficulty-select">
                    <option value="beginner" className="bg-slate-800">Beginner</option>
                    <option value="intermediate" className="bg-slate-800">Intermediate</option>
                    <option value="advanced" className="bg-slate-800">Advanced</option>
                  </select>
                </div>
                <div><label className="text-xs text-slate-400 block mb-1">Estimated Time</label><Input value={projectForm.estimated_time} onChange={e => setProjectForm(f => ({ ...f, estimated_time: e.target.value }))} placeholder="e.g. 4-6 hours" className="bg-white/10 border-white/20 text-white" /></div>
                <div><label className="text-xs text-slate-400 block mb-1">Order</label><Input type="number" value={projectForm.order} onChange={e => setProjectForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} className="bg-white/10 border-white/20 text-white" /></div>
              </div>
              <div><label className="text-xs text-slate-400 block mb-1">Prerequisites</label><Input value={projectForm.prerequisites} onChange={e => setProjectForm(f => ({ ...f, prerequisites: e.target.value }))} placeholder="Module 1 & Module 2, Basic Linux knowledge" className="bg-white/10 border-white/20 text-white" /></div>

              {/* Section-wise Editor */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-slate-400">Sections *</label>
                  <Button size="sm" onClick={() => setProjectForm(f => ({ ...f, sections: [...f.sections, { title: '', content: '' }] }))} className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 h-7 text-xs" data-testid="add-section-btn">
                    <Plus className="w-3 h-3 mr-1" />Add Section
                  </Button>
                </div>
                <div className="space-y-3">
                  {projectForm.sections.map((sec, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded bg-orange-500/20 text-orange-300 flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</span>
                        <Input
                          value={sec.title}
                          onChange={e => { const s = [...projectForm.sections]; s[idx] = { ...s[idx], title: e.target.value }; setProjectForm(f => ({ ...f, sections: s })); }}
                          placeholder="Section title (e.g. PHASE 1: INITIAL SETUP)"
                          className="bg-white/10 border-white/20 text-white text-sm h-8 font-semibold"
                          data-testid={`section-title-${idx}`}
                        />
                        <div className="flex gap-1 flex-shrink-0">
                          {idx > 0 && <Button size="sm" variant="ghost" onClick={() => { const s = [...projectForm.sections]; [s[idx-1], s[idx]] = [s[idx], s[idx-1]]; setProjectForm(f => ({ ...f, sections: s })); }} className="text-slate-400 hover:text-white h-7 w-7 p-0" title="Move up">↑</Button>}
                          {idx < projectForm.sections.length - 1 && <Button size="sm" variant="ghost" onClick={() => { const s = [...projectForm.sections]; [s[idx], s[idx+1]] = [s[idx+1], s[idx]]; setProjectForm(f => ({ ...f, sections: s })); }} className="text-slate-400 hover:text-white h-7 w-7 p-0" title="Move down">↓</Button>}
                          {projectForm.sections.length > 1 && <Button size="sm" variant="ghost" onClick={() => { const s = projectForm.sections.filter((_, i) => i !== idx); setProjectForm(f => ({ ...f, sections: s })); }} className="text-red-400 hover:text-red-300 h-7 w-7 p-0" title="Remove"><Trash2 className="w-3 h-3" /></Button>}
                        </div>
                      </div>
                      <textarea
                        value={sec.content}
                        onChange={e => { const s = [...projectForm.sections]; s[idx] = { ...s[idx], content: e.target.value }; setProjectForm(f => ({ ...f, sections: s })); }}
                        rows={4}
                        placeholder="Section content — steps, commands, configurations..."
                        className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 text-sm font-mono resize-y focus:ring-2 focus:ring-orange-500 focus:border-orange-500 placeholder-slate-500"
                        data-testid={`section-content-${idx}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">Har section student ko ek step ki tarah dikhega — Next/Previous buttons ke saath navigate kar sakta hai</p>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSaveProject} disabled={savingProject} className="bg-gradient-to-r from-orange-500 to-red-500 text-white" data-testid="save-project-btn">
                  <Save className="w-4 h-4 mr-2" />{savingProject ? 'Saving...' : editingProject ? 'Update' : 'Create Project'}
                </Button>
                <Button variant="outline" onClick={resetProjectForm} className="border-white/20 text-slate-300">Cancel</Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Projects List */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><FolderGit2 className="w-5 h-5 text-orange-400" />All Projects<Badge className="bg-orange-500/20 text-orange-300 ml-2">{adminProjects.length}</Badge></CardTitle></CardHeader>
        <CardContent>
          {projectsLoading ? <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-orange-500 animate-spin" /></div> : adminProjects.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No projects yet. Click "Add" to create one.</p>
          ) : (
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-white/10">
                  <TableHead className="text-slate-300">#</TableHead>
                  <TableHead className="text-slate-300">Title</TableHead>
                  <TableHead className="text-slate-300">Category</TableHead>
                  <TableHead className="text-slate-300">Difficulty</TableHead>
                  <TableHead className="text-slate-300">Status</TableHead>
                  <TableHead className="text-slate-300">Free</TableHead>
                  <TableHead className="text-slate-300 text-center">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>{adminProjects.map((p, idx) => (
                  <TableRow key={p.id} className="border-white/10">
                    <TableCell className="text-slate-500">{idx + 1}</TableCell>
                    <TableCell className="text-white font-medium max-w-[200px] truncate">{p.title}</TableCell>
                    <TableCell><Badge className="bg-blue-500/20 text-blue-300">{p.category}</Badge></TableCell>
                    <TableCell><Badge className={p.difficulty === 'beginner' ? 'bg-emerald-500/20 text-emerald-300' : p.difficulty === 'intermediate' ? 'bg-amber-500/20 text-amber-300' : 'bg-red-500/20 text-red-300'}>{p.difficulty}</Badge></TableCell>
                    <TableCell><Badge className={p.is_enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}>{p.is_enabled ? 'Enabled' : 'Disabled'}</Badge></TableCell>
                    <TableCell><Switch checked={p.is_free || false} onCheckedChange={() => handleToggleFreeProject(p.id)} /></TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Switch checked={p.is_enabled} onCheckedChange={() => handleToggleProject(p.id)} />
                        <Button size="sm" variant="ghost" onClick={() => handleEditProject(p)} className="text-blue-400 hover:bg-blue-500/20" title="Edit"><Edit className="w-3 h-3" /></Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteProject(p.id)} className="bg-red-500/20 hover:bg-red-500/30 text-red-300"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
